/* eslint-disable no-restricted-syntax -- Local building block for the stores screens on the admin's own design system (shared/rd); see apps/admin/AGENTS.md. */
import { useContext, useEffect, useId, useLayoutEffect, useRef, type ComponentProps } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft } from "lucide-react";
import { Badge, Button, Drawer } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiContext, StateContext } from "./drawer-panel-host";

/**
 * A form's drawer that never stacks on another drawer: inside a host (the
 * big store-details drawer, see `useDrawerPanels`) it takes over the host's
 * body and bar; anywhere else it is an ordinary RD Drawer.
 */
type PanelDrawerProps = Omit<ComponentProps<typeof Drawer>, "crumb" | "dismissible" | "className"> & {
  /** The host's back control ("Back to Nour Bags"). */
  backLabel?: string;
};

/** A form's drawer: inside a host it swaps the host's body instead. */
export function PanelDrawer({ open, onClose, closeLabel, badge, title, sub, actions, width, children, backLabel }: PanelDrawerProps) {
  const api = useContext(ApiContext);
  const state = useContext(StateContext);
  const t = useTranslations();
  const id = useId();
  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!api || !open) return;
    api.open(id, () => onCloseRef.current?.());
    return () => api.close(id);
  }, [api, open, id]);

  if (!api) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        closeLabel={closeLabel}
        badge={badge}
        title={title}
        sub={sub}
        actions={actions}
        width={width}
      >
        {children}
      </Drawer>
    );
  }
  if (!open || state.activeId !== id || !state.bar || !state.body) return null;
  return (
    <>
      {createPortal(actions ?? null, state.bar)}
      {createPortal(
        <section aria-label={typeof title === "string" ? title : undefined} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <Button autoFocus variant="ghost" size="sm" muted icon={ArrowLeft} className="rd-flip-rtl" disabled={!onClose} onClick={() => onClose?.()}>
              {backLabel ?? t(keys.common.back)}
            </Button>
          </div>
          {title || badge ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {badge ? <Badge id={badge} size={36} /> : null}
              <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <h2 className="rd-drawer-title" style={{ margin: 0 }}>
                  {title}
                </h2>
                {sub ? <span className="rd-drawer-sub">{sub}</span> : null}
              </div>
            </div>
          ) : null}
          <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 560 }}>{children}</div>
        </section>,
        state.body,
      )}
    </>
  );
}
